import { css } from '@emotion/react';
import { debounce, List, ListItemButton, ListItemText, Paper, Popper, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { FieldFactory } from '../../../components/Form/FieldFactory';
import { useFocusElement2 } from '../../../hooks/useFocusElement';
import { waterfallFormulaSchema } from '../../../schemas/FundWaterfall.schema';
import { validateWaterfallFormula } from '../../../services/queries/MaggieFundQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { colors } from '../../../theme/colors';
import { schemaToFormFields } from '../../../util/schema-utils';
import { useWaterfallConfig } from './useWaterfallConfig';
import { CalculatedField } from './useWaterfallFormulas';

export const DebounceTime = 1000;

const FORMULA_FUNCTIONS = ['MIN', 'MAX'];

const FormulaFieldCss = css`
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 0.2rem;
  &&&& .MuiFormControl-root {
    margin: 0;
    box-shadow: inset 0 0 0 2px ${colors.secondary[40]};
    width: 100%;
  }
  &&&& .formula-label {
    align-self: start;
    padding-top: 0.3rem;
  }
  &&&& input::placeholder {
    color: var(--mui-palette-text-secondary);
    opacity: 1;
  }
`;

const SuggestionsCss = css`
  z-index: 1300;
  .MuiList-root {
    max-height: 200px;
    overflow-y: auto;
    padding: 0.2rem 0;
  }
  .MuiListItemButton-root {
    padding: 0.15rem 0.5rem;
    &.Mui-selected {
      background-color: ${colors.primary[10]};
    }
  }
  .suggestion-text {
    font-size: 0.8rem;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  }
  .suggestion-type {
    font-size: 0.65rem;
    color: ${colors.neutral[50]};
    margin-left: 0.5rem;
  }
`;

interface Suggestion {
  text: string;
  insertText: string;
  type: 'variable' | 'function';
}

export function WaterfallFormulaField({
  field,
  availableVariables,
}: {
  field: CalculatedField | null;
  availableVariables?: Set<string>;
}) {
  const { formField, formProps, error, setValue } = useFormulaField({ field });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const allSuggestions: Suggestion[] = useMemo(() => {
    const vars = [...(availableVariables ?? [])].sort().map(
      (v): Suggestion => ({
        text: v,
        insertText: v,
        type: 'variable',
      })
    );
    const fns = FORMULA_FUNCTIONS.map(
      (fn): Suggestion => ({
        text: `${fn}()`,
        insertText: `${fn}(`,
        type: 'function',
      })
    );
    return [...vars, ...fns];
  }, [availableVariables]);

  useFocusElement2(containerRef, 'input', { preventScroll: true });

  useEffect(() => {
    const input = containerRef.current?.querySelector('input');
    if (input) {
      inputRef.current = input;
    }
  });

  const updateSuggestions = useCallback(
    (input: HTMLInputElement) => {
      const { word } = getWordAtCursor(input);
      if (!word || word.length < 1) {
        setSuggestions([]);
        setAnchorEl(null);
        return;
      }
      const lower = word.toLowerCase();
      const matches = allSuggestions.filter(
        (s) => s.text.toLowerCase().startsWith(lower) && s.insertText !== word
      );
      setSuggestions(matches);
      setSelectedIndex(0);
      setAnchorEl(matches.length > 0 ? input : null);
    },
    [allSuggestions]
  );

  const insertSuggestion = useCallback(
    (suggestion: Suggestion) => {
      const input = inputRef.current;
      if (!input) {
        return;
      }
      const { start, end } = getWordAtCursor(input);
      const value = input.value;
      const newValue = value.slice(0, start) + suggestion.insertText + value.slice(end);

      setValue(newValue);
      setSuggestions([]);
      setAnchorEl(null);

      requestAnimationFrame(() => {
        const cursorPos = start + suggestion.insertText.length;
        input.setSelectionRange(cursorPos, cursorPos);
        input.focus();
      });
    },
    [setValue]
  );

  // Attach keydown directly to the input since FieldFactory doesn't forward onKeyDown
  const suggestionsRef = useRef(suggestions);
  const selectedIndexRef = useRef(selectedIndex);
  const insertSuggestionRef = useRef(insertSuggestion);
  suggestionsRef.current = suggestions;
  selectedIndexRef.current = selectedIndex;
  insertSuggestionRef.current = insertSuggestion;

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }
    const handler = (e: globalThis.KeyboardEvent) => {
      const sug = suggestionsRef.current;
      if (sug.length === 0) {
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, sug.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertSuggestionRef.current(sug[selectedIndexRef.current]);
      } else if (e.key === 'Escape') {
        setSuggestions([]);
        setAnchorEl(null);
      }
    };
    input.addEventListener('keydown', handler);
    return () => input.removeEventListener('keydown', handler);
  }, []); // handler reads exclusively from refs, no need to re-attach

  const enhancedFormProps = {
    ...formProps,
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      formProps.onChange(event);
      requestAnimationFrame(() => {
        if (inputRef.current) {
          updateSuggestions(inputRef.current);
        }
      });
    },
    onBlur: () => {
      setTimeout(() => {
        setSuggestions([]);
        setAnchorEl(null);
      }, 150);
    },
  } as ControllerRenderProps;

  return (
    <div css={FormulaFieldCss} ref={containerRef}>
      <Typography variant='body1' className='formula-label' fontWeight={'bold'} color='primary'>
        𝑓<sub>x</sub>&nbsp;
      </Typography>
      <div>
        <FieldFactory formField={formField} formProps={enhancedFormProps} />
        <Popper
          open={suggestions.length > 0}
          anchorEl={anchorEl}
          placement='bottom-start'
          css={SuggestionsCss}
        >
          <Paper elevation={4}>
            <List dense>
              {suggestions.map((suggestion, i) => (
                <ListItemButton
                  key={suggestion.text}
                  selected={i === selectedIndex}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertSuggestion(suggestion);
                  }}
                >
                  <ListItemText
                    primary={
                      <>
                        <span className='suggestion-text'>{suggestion.text}</span>
                        <span className='suggestion-type'>{suggestion.type}</span>
                      </>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
        <Typography
          variant='caption'
          color={error ? 'error' : 'transparent'}
          sx={{ display: 'block', height: '0.8rem' }}
        >
          {error}
        </Typography>
      </div>
    </div>
  );
}

function getWordAtCursor(input: HTMLInputElement): { word: string; start: number; end: number } {
  const pos = input.selectionStart ?? 0;
  const value = input.value;
  let start = pos;
  while (start > 0 && /[\w]/.test(value[start - 1])) {
    start--;
  }
  let end = pos;
  while (end < value.length && /[\w]/.test(value[end])) {
    end++;
  }
  return { word: value.slice(start, end), start, end };
}

export function useFormulaField({
  field,
  delay = DebounceTime,
}: {
  field: CalculatedField | null;
  delay?: number;
}) {
  let formField = schemaToFormFields(waterfallFormulaSchema())[0];
  if (!field) {
    formField = { ...formField, disabled: true, placeholder: 'Select a cell to view and edit the formula' };
  }
  const { updateFormula } = useWaterfallConfig();
  const [error, setError] = useState<string>('');
  const [formula, setFormula] = useState<string | null>(field?.formula ?? null);

  const autoSave = useCallback(
    async (field: CalculatedField | null, newValue: string) => {
      if (!field) {
        return;
      }

      try {
        const validationRes = await validateWaterfallFormula(newValue);
        if (!validationRes.isValidFormula) {
          setError(validationRes.message);
          return;
        }
        updateFormula({ tierField: field, newFormula: newValue });
        setError('');
      } catch (error) {
        console.error('Error validating formula', error);
        setError(getErrorMessage(error, 'Error validating formula'));
      }
    },
    [updateFormula]
  );

  const autoSaveRef = useRef(autoSave);
  useEffect(() => {
    autoSaveRef.current = autoSave;
  }, [autoSave]);
  const debouncedAutoSaveRef = useRef(
    debounce((...args: Parameters<typeof autoSave>) => autoSaveRef.current(...args), delay)
  );

  const setValue = useCallback(
    (newValue: string) => {
      setFormula(newValue);
      debouncedAutoSaveRef.current(field, newValue);
    },
    [field]
  );

  const formProps = {
    value: formula,
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
    },
  } as ControllerRenderProps;

  return {
    formField,
    formProps,
    error,
    setValue,
  };
}
